import { Box, Divider, Grid, Stack, Typography } from '@mui/material';
import { get } from 'lodash-es';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { lineItemFormText } from '../../constants/lineItems';
import { useConstants } from '../../hooks/useConstants';
import { useContractConfigurationContext } from '../../hooks/useContractConfigurationContext';
import { addCommas } from '../../util/helpers';
import mapContractToForm from '../../util/mapContractToForm';
import BudgetRatesFields from '../Contracts/BudgetRatesFields';
import AccordionSection from '../forms/AccordionSection';
import Checkbox from '../forms/Checkbox';
import CheckboxArray from '../forms/CheckboxArray';
import FormSection from '../forms/FormSection';
import FormSubsection from '../forms/FormSubsection';
import RadioGroup from '../forms/RadioGroup';
import Select from '../forms/Select';
import TextField from '../forms/TextField';
import BreakingFlex from '../shared/BreakingFlex';

function LineItemBudgetRates() {
  const contractConfigurationContext = useContractConfigurationContext();
  const contractData = get(contractConfigurationContext, 'contractData', {});
  const contractForm = mapContractToForm(contractData);
  const { constants } = useConstants();

  const selectionAlgorithmOptions = get(constants, 'INSERTION_ORDER.PID_TYPES_ARRAY', []).map((item) => ({
    value: item[1],
    label: item[0],
  }));

  const pacingStrategyList = get(constants, 'CONTRACT.PACING_STRATEGIES', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));

  const form = useFormContext();
  const { setValue, resetField, watch } = form;

  const setValueOptions = { shouldValidate: true, shouldDirty: true };

  const [frequencyCapEnabled, performanceCapEnabled, dynamicSpendPacing, impressionBudget, totalImpressions] = watch([
    'frequencyCapEnabled',
    'performanceCapEnabled',
    'dynamicSpendPacing',
    'impressionBudget',
    'totalImpressions',
  ]);

  const handleChangeDynamicSpendPacing = (e) => {
    if (!e.target.value) {
      setValue('pacingStrategies', [], setValueOptions);
    }
    return e.target.value === 'true';
  };

  const handleFrequencyCapEnabledChange = (e) => {
    if (!e.target.checked) {
      resetField('maxUserViewsPerPeriod');
      resetField('frequencyCapping');
    }
    return e.target.checked;
  };

  const handlePerformanceCapEnabledChange = (e) => {
    if (!e.target.checked) {
      resetField('maxUserClicksPerPeriod');
      resetField('clickFrequencyCapping');
    }
    return e.target.checked;
  };

  return (
    <FormSection title={lineItemFormText.sectionTitles.budgetRates}>
      <BudgetRatesFields
        bidType={contractForm.bidType}
        fixedPricingRate={!contractForm.multiRate && contractForm.pricingRate}
        currencyOptions={{ symbol: '', precision: 2 }}
      />
      <FormSubsection title={lineItemFormText.sectionTitles.timing}>
        <BreakingFlex breakpoint="md">
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, pb: 3, pl: 2 }}>
            <Checkbox
                name="frequencyCapEnabled"
                label="Enable Frequency Cap"
                normalizeOutput={handleFrequencyCapEnabledChange}
            />
          </Box>
          {frequencyCapEnabled && (
              <>
                <TextField
                    label="Max Impressions"
                    name="maxUserViewsPerPeriod"
                    disabled={!frequencyCapEnabled}
                    type="number"
                />
                <Select
                    disabled={!frequencyCapEnabled}
                    name="frequencyCapping"
                    label="Frequency"
                    sx={{ minWidth: 225 }}
                    options={[
                      { value: 'daily', label: 'Per Day' },
                      { value: 'weekly', label: 'Per Week' },
                      { value: 'monthly', label: 'Per Month' },
                    ]}
                />
              </>
          )}
        </BreakingFlex>
        <Typography variant="body2" sx={{ mb: 2, color: '#616161' }}>
          {`(Setting a low frequency cap might impact the campaign's delivery. We recommend applying a daily cap of at
              least 3 impressions.)`}
        </Typography>
        <BreakingFlex breakpoint="md">
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, pb: 3, pl: 2 }}>
            <Checkbox
                name="performanceCapEnabled"
                label="Enable Performance Cap"
                normalizeOutput={handlePerformanceCapEnabledChange}
            />
          </Box>
          {performanceCapEnabled && (
              <>
                <TextField
                    label="Max Impressions"
                    name="maxUserClicksPerPeriod"
                    disabled={!performanceCapEnabled}
                    type="number"
                />
                <Select
                    disabled={!performanceCapEnabled}
                    name="clickFrequencyCapping"
                    label="Frequency"
                    sx={{ minWidth: 225 }}
                    options={[
                      { value: 'daily', label: 'Per Day' },
                      { value: 'weekly', label: 'Per Week' },
                    ]}
                />
              </>
          )}
        </BreakingFlex>
      </FormSubsection>
      <Grid item xs={12}>
        <Divider variant="middle" />
      </Grid>
      <AccordionSection>
        <Stack px={2}>
          <FormSubsection title={lineItemFormText.subsectionTitles.selectionAlgorithm}>
            <BreakingFlex breakpoint="md">
              <Stack p={2} pt={0}>
                <Select name="pidType" label="Selection Algorithm" options={selectionAlgorithmOptions} />
              </Stack>
            </BreakingFlex>
          </FormSubsection>
          <FormSubsection title={lineItemFormText.subsectionTitles.spendPacing}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 2, pb: 3, pl: 2 }}>
              <RadioGroup
                name="dynamicSpendPacing"
                normalizeOutput={handleChangeDynamicSpendPacing}
                options={[
                  { value: true, label: 'Enabled' },
                  { value: false, label: 'Disabled' },
                ]}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                  <Typography color={!dynamicSpendPacing ? 'text.disabled' : null}>(&nbsp;</Typography>
                  <CheckboxArray row name="pacingStrategies" disabled={!dynamicSpendPacing} options={pacingStrategyList} />
                  <Typography color={!dynamicSpendPacing ? 'text.disabled' : null}>&nbsp;)</Typography>
                </Box>
                <BreakingFlex breakpoint="md" sx={{ alignItems: 'flex-start' }}>
                  <TextField
                    label="Daily Impression Limit"
                    name="dailyImpressionLimit"
                    disabled={dynamicSpendPacing}
                    type="number"
                    sx={{ minWidth: '200px' }}
                  />
                  <Typography variant="caption" color="gray">
                    {lineItemFormText.impressionLimitTip}
                  </Typography>
                </BreakingFlex>
              </Box>
            </Box>
          </FormSubsection>
          <FormSubsection title={lineItemFormText.subsectionTitles.bidding}>
            <BreakingFlex breakpoint="md" sx={{ mb: 2 }}>
              <TextField label="Bid Priority" name="bidPriority" type="number" />
            </BreakingFlex>
          </FormSubsection>
          <FormSubsection title={lineItemFormText.subsectionTitles.deliveryDiagnostics}>
            <BreakingFlex breakpoint="md" sx={{ pl: 2, gap: 6 }}>
              <Stack>
                <Typography variant="caption" color="text.secondary">
                  {lineItemFormText.deliveryDiagnostics.impressionBudgetLabel}
                </Typography>
                <Typography variant="body1" data-testid="impression-budget-value">
                  {addCommas(impressionBudget ?? 0)}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="caption" color="text.secondary">
                  {lineItemFormText.deliveryDiagnostics.deliveredImpressionsLabel}
                </Typography>
                <Typography variant="body1" data-testid="delivered-impressions-value">
                  {addCommas(totalImpressions ?? 0)}
                </Typography>
              </Stack>
            </BreakingFlex>
            <Typography variant="caption" color="gray" sx={{ mt: 1, mb: 2, pl: 2 }}>
              {lineItemFormText.deliveryDiagnostics.tip}
            </Typography>
          </FormSubsection>
        </Stack>
      </AccordionSection>
    </FormSection>
  );
}

export default LineItemBudgetRates;
